import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { apiError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { resolveUnits, fmtWind } from '@/lib/units';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  BellRing, Bell, MapPin, Trash2, Loader2, Wind, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

export const WindAlertsWidget = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const units = resolveUnits(user);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/wind-alerts');
      setAlerts(data || []);
    } catch (e) {
      // Non-blocking widget: surface for debugging without disrupting the dashboard.
      console.warn('[WindAlertsWidget] failed to load wind alerts', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (a, next) => {
    setBusyId(a.id);
    try {
      const { data } = await api.put(`/wind-alerts/${a.id}`, {
        spot_id: a.spot_id, min_wind_knots: a.min_wind_knots, active: next,
      });
      setAlerts((prev) => prev.map((x) => (x.id === a.id ? data : x)));
    } catch (e) {
      toast.error(apiError(e, t('windAlert.toast.error')));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a) => {
    setBusyId(a.id);
    try {
      await api.delete(`/wind-alerts/${a.id}`);
      setAlerts((prev) => prev.filter((x) => x.id !== a.id));
      toast.success(t('windAlert.toast.removed'));
    } catch (e) {
      toast.error(apiError(e, t('windAlert.toast.error')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="kite-card p-5" data-testid="wind-alerts-widget">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <BellRing className="w-4 h-4 text-[#2EE59D]" /> {t('windAlert.widgetTitle')}
        </h3>
        <Link to="/spots" className="text-xs text-[#48CAE4] hover:text-[#00B4D8] inline-flex items-center gap-1" data-testid="wind-alerts-add-link">
          {t('windAlert.addMore')} <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {loading ? (
        <div className="py-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#00B4D8]" /></div>
      ) : alerts.length === 0 ? (
        <div className="text-center py-6" data-testid="wind-alerts-empty">
          <div className="w-12 h-12 rounded-2xl bg-[#00B4D8]/12 flex items-center justify-center mx-auto"><Wind className="w-6 h-6 text-[#00B4D8]" /></div>
          <p className="text-sm text-muted-foreground mt-3 max-w-xs mx-auto">{t('windAlert.widgetEmpty')}</p>
          <Button asChild variant="outline" size="sm" className="mt-3 border-white/15 bg-white/5 hover:bg-white/10 rounded-lg">
            <Link to="/spots" data-testid="wind-alerts-browse-btn">{t('windAlert.browseSpots')}</Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {alerts.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03] border border-white/5" data-testid={`wind-alert-item-${a.spot_id}`}>
              <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ background: a.active ? 'rgba(46,229,157,0.12)' : 'rgba(255,255,255,0.05)' }}>
                {a.active ? <BellRing className="w-4 h-4 text-[#2EE59D]" /> : <Bell className="w-4 h-4 text-muted-foreground" />}
              </span>
              <Link to={`/spots/${a.spot_id}`} className="min-w-0 flex-1 group">
                <div className="font-medium text-sm truncate group-hover:text-[#48CAE4] transition-colors flex items-center gap-1">
                  <MapPin className="w-3 h-3 text-muted-foreground" /> {a.spot_name}
                </div>
                <div className="text-xs text-muted-foreground font-data">
                  {t('windAlert.fromThreshold', { value: fmtWind(a.min_wind_knots, units) })}
                </div>
              </Link>
              <Switch checked={a.active} onCheckedChange={(v) => toggle(a, v)} disabled={busyId === a.id} data-testid={`wind-alert-toggle-${a.spot_id}`} />
              <button
                onClick={() => remove(a)}
                disabled={busyId === a.id}
                className="text-muted-foreground hover:text-[#FF6B35] transition-colors p-1"
                data-testid={`wind-alert-delete-${a.spot_id}`}
                aria-label={t('windAlert.remove')}
              >
                {busyId === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
